import { createFileRoute } from '@tanstack/react-router'
import { PlusIcon } from 'lucide-react'
import { useMemo, useState } from 'react'

import type { View } from 'react-big-calendar'
import type {
    WorkActivity,
    WorkActivityType,
} from '@/lib/api/work-activity-api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { WorkActivityCalendar } from '@/components/work-activities/work-activity-calendar'
import { WorkActivityDialog } from '@/components/work-activities/work-activity-dialog'
import { useAuth } from '@/hooks/use-auth'
import { useWorkActivities } from '@/hooks/use-work-activities-query'
import { m } from '@/lib/paraglide/messages'
import { cn } from '@/lib/utils'
import { workActivityTypeLabel } from '@/lib/work-activity'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/work-activities/calendar',
)({
    component: WorkActivityCalendarPage,
})

const ALL_TYPES: WorkActivityType[] = [
    'meeting',
    'business_trip',
    'event',
    'task',
]

/**
 * Khoảng cần nạp cho khung nhìn đang xem, nới rộng hai đầu.
 *
 * Nới ±1 tháng vì lưới tháng còn hiển thị vài ngày của tháng trước và tháng sau;
 * cắt đúng mốc đầu/cuối tháng là mấy ô rìa trống trơn dù thực tế có lịch.
 */
function rangeFor(date: Date): { from: string; to: string } {
    const from = new Date(date.getFullYear(), date.getMonth() - 1, 1)
    const to = new Date(date.getFullYear(), date.getMonth() + 2, 0, 23, 59, 59)
    return { from: from.toISOString(), to: to.toISOString() }
}

function WorkActivityCalendarPage() {
    const { user, hasPermission } = useAuth()
    const [view, setView] = useState<View>('month')
    const [date, setDate] = useState(() => new Date())
    const [visibleTypes, setVisibleTypes] =
        useState<WorkActivityType[]>(ALL_TYPES)

    const [dialogOpen, setDialogOpen] = useState(false)
    const [editing, setEditing] = useState<WorkActivity | null>(null)
    const [slotStart, setSlotStart] = useState<string | undefined>()

    const canCreate = hasPermission('work_activities.create')

    const range = useMemo(() => rangeFor(date), [date])
    const { data, isError } = useWorkActivities({
        from: range.from,
        to: range.to,
    })

    const activities = useMemo(
        () => (data ?? []).filter((a) => visibleTypes.includes(a.type)),
        [data, visibleTypes],
    )

    const toggleType = (type: WorkActivityType) => {
        setVisibleTypes((prev) =>
            prev.includes(type)
                ? prev.filter((t) => t !== type)
                : [...prev, type],
        )
    }

    const openCreate = (startInput?: string) => {
        setEditing(null)
        setSlotStart(startInput)
        setDialogOpen(true)
    }

    return (
        <div className="space-y-4 page-pad">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h1 className="text-lg font-semibold">
                    {m.work_activity_calendar_title()}
                </h1>
                {canCreate && (
                    <Button onClick={() => openCreate()}>
                        <PlusIcon className="size-4" />
                        {m.work_activity_create_button()}
                    </Button>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <span className="text-muted-foreground text-xs">
                    {m.work_activity_calendar_filter_label()}
                </span>
                {ALL_TYPES.map((type) => {
                    const active = visibleTypes.includes(type)
                    return (
                        <button
                            key={type}
                            type="button"
                            onClick={() => toggleType(type)}
                            aria-pressed={active}
                        >
                            <Badge
                                variant={active ? 'default' : 'outline'}
                                className={cn(
                                    !active && 'text-muted-foreground',
                                )}
                            >
                                {workActivityTypeLabel[type]()}
                            </Badge>
                        </button>
                    )
                })}
            </div>

            {isError && (
                <p className="text-destructive text-sm">
                    {m.work_activity_calendar_load_error()}
                </p>
            )}

            <WorkActivityCalendar
                activities={activities}
                view={view}
                date={date}
                onViewChange={setView}
                onDateChange={setDate}
                onSelectActivity={(activity) => {
                    setEditing(activity)
                    setSlotStart(undefined)
                    setDialogOpen(true)
                }}
                onSelectSlot={(startInput) => {
                    if (canCreate) openCreate(startInput)
                }}
            />

            <WorkActivityDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                editing={editing}
                defaultType="meeting"
                defaultStartsAt={slotStart}
                canEdit={!editing || editing.created_by === user?.id}
            />
        </div>
    )
}
